import { LOCALE_TO_SLUG, type LocaleCode } from "@/lib/labels/types";
import type { LivescoreBoard, LivescoreFilter, LivescoreSport } from "@/views/livescore/utils/livescore";

/**
 * Query contract for `GET /api/live/livescore`. Pure parsing, shared by the
 * route (validation) and the browser client (URL building), so both sides
 * agree on the canonical parameter order and the edge cache key.
 */
export interface LivescoreQuery {
    date: string;
    locale: LocaleCode;
    /** Omitted = every sport (news-blog ticker / widget). */
    sport?: LivescoreSport;
    /** Only upcoming / live / finished reach the API; the rest are client-side filters. */
    filter: LivescoreFilter;
    /**
     * Restrict the board to competitions: the tenant's popular list ("popular", resolved
     * server-side per sport) or an explicit list of native competition ids (max
     * `MAX_COMPETITION_IDS`, sorted so order never changes the cache key). Absent = the whole day.
     */
    competitions?: "popular" | string[];
    /**
     * Hydration level. Absent = full (teams and competitions). "competitions" resolves only the
     * competition entities — the shape the collapsed tail of the livescore page renders — and
     * never touches the day's ~1,500 team ids.
     */
    hydrate?: "competitions";
}

const SPORTS: readonly LivescoreSport[] = ["football", "basketball", "tennis", "hockey", "american-football"];
const FILTERS: readonly LivescoreFilter[] = ["all", "popular", "upcoming", "live", "finished", "my-leagues"];
const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const COMPETITION_ID_PATTERN = /^[a-z]{2}:c:\d+$/;
/** Favourites are uncapped in the picker; the my-leagues stage sends the first 20. */
export const MAX_COMPETITION_IDS = 20;

export function parseLivescoreQuery(params: URLSearchParams): LivescoreQuery | null {
    const date = params.get("date") ?? "";
    const locale = params.get("locale") ?? "";
    const sport = params.get("sport") ?? undefined;
    const filter = params.get("filter") ?? "all";
    if (!DATE_PATTERN.test(date) || Number.isNaN(Date.parse(date))) return null;
    if (!(locale in LOCALE_TO_SLUG)) return null;
    if (sport !== undefined && !SPORTS.includes(sport as LivescoreSport)) return null;
    if (!FILTERS.includes(filter as LivescoreFilter)) return null;
    const competitions = parseCompetitions(params.get("competitions"));
    if (competitions === null) return null;
    const hydrateRaw = params.get("hydrate");
    if (hydrateRaw !== null && hydrateRaw !== "competitions") return null;
    return {
        date,
        locale: locale as LocaleCode,
        sport: sport as LivescoreSport | undefined,
        filter: filter as LivescoreFilter,
        ...(competitions !== undefined && { competitions }),
        ...(hydrateRaw === "competitions" && { hydrate: "competitions" as const }),
    };
}

/** undefined = parameter absent; null = present but invalid. */
function parseCompetitions(raw: string | null): "popular" | string[] | null | undefined {
    if (raw === null) return undefined;
    if (raw === "popular") return "popular";
    const ids = raw.split(",").map((id) => id.trim());
    if (ids.length === 0 || ids.length > MAX_COMPETITION_IDS) return null;
    if (!ids.every((id) => COMPETITION_ID_PATTERN.test(id))) return null;
    return [...new Set(ids)].sort();
}

/** Canonical query string: fixed key order, filters that the API ignores collapse to "all". */
export function buildLivescoreQuery(query: LivescoreQuery): string {
    const apiFilter =
        query.filter === "upcoming" || query.filter === "live" || query.filter === "finished" ? query.filter : "all";
    const params = new URLSearchParams();
    params.set("date", query.date);
    params.set("locale", query.locale);
    if (query.sport) params.set("sport", query.sport);
    params.set("filter", apiFilter);
    if (query.competitions === "popular") params.set("competitions", "popular");
    else if (query.competitions?.length) params.set("competitions", [...query.competitions].sort().join(","));
    if (query.hydrate === "competitions") params.set("hydrate", "competitions");
    return params.toString();
}

/**
 * A board rendered on the server together with the query it answers. The
 * client pollers accept one as `initialBoard`; it is used only when the
 * client's first query matches (see `seedMatches`), otherwise they fetch on
 * mount exactly as without a seed.
 */
export interface LivescoreBoardSeed {
    query: LivescoreQuery;
    board: LivescoreBoard;
}

/** True when a seed answers the given query (same canonical query string). */
export function seedMatches(seed: LivescoreBoardSeed | undefined, query: LivescoreQuery): seed is LivescoreBoardSeed {
    return seed !== undefined && buildLivescoreQuery(seed.query) === buildLivescoreQuery(query);
}
